import React, { createContext, useContext, useState } from 'react';

const ExamContext = createContext();

export const useExam = () => {
  const context = useContext(ExamContext);
  if (!context) {
    throw new Error('useExam must be used within an ExamProvider');
  }
  return context;
};

export const ExamProvider = ({ children }) => {
  const [exams] = useState([
    {
      id: '1',
      title: 'NAPLAN Year 9 Mathematics',
      description: 'Practice test for NAPLAN mathematics assessment',
      duration: 60,
      questions: 40,
      category: 'Mathematics',
      difficulty: 'Medium'
    },
    {
      id: '2',
      title: 'NAPLAN Year 9 Reading',
      description: 'Reading comprehension practice test',
      duration: 45,
      questions: 35,
      category: 'Reading',
      difficulty: 'Medium'
    },
    {
      id: '3',
      title: 'NAPLAN Year 9 Writing',
      description: 'Writing skills assessment practice',
      duration: 40,
      questions: 1,
      category: 'Writing',
      difficulty: 'Medium'
    },
    {
      id: '4',
      title: 'Mathematics Advanced',
      description: 'Challenging mathematics problems',
      duration: 90,
      questions: 50,
      category: 'Mathematics',
      difficulty: 'Hard'
    }
  ]);

  const [examQuestions] = useState({
    '1': [
      {
        id: 'q1',
        question: 'What is the value of x in the equation 2x + 5 = 15?',
        options: ['5', '10', '7.5', '12.5'],
        correctAnswer: 0,
        type: 'multiple-choice'
      },
      {
        id: 'q2',
        question: 'Calculate the area of a rectangle with length 8cm and width 5cm.',
        options: ['40 cm²', '13 cm²', '26 cm²', '80 cm²'],
        correctAnswer: 0,
        type: 'multiple-choice'
      },
      {
        id: 'q3',
        question: 'What is 25% of 200?',
        options: ['25', '50', '75', '100'],
        correctAnswer: 1,
        type: 'multiple-choice'
      },
      {
        id: 'q4',
        question: 'Simplify: 3x + 2x - x',
        options: ['4x', '5x', '6x', '2x'],
        correctAnswer: 0,
        type: 'multiple-choice'
      },
      {
        id: 'q5',
        question: 'What is the next number in the sequence: 2, 4, 8, 16, ?',
        options: ['24', '32', '20', '28'],
        correctAnswer: 1,
        type: 'multiple-choice'
      }
    ],
    '2': [
      {
        id: 'q1',
        question: 'Read the following passage and answer: What is the main theme of the text?',
        passage: 'The sun rose over the quiet village, casting golden rays on the sleeping houses. Birds began to sing their morning songs, and the world slowly came to life.',
        options: ['Sunrise', 'Morning routine', 'Nature awakening', 'Village life'],
        correctAnswer: 2,
        type: 'multiple-choice'
      },
      {
        id: 'q2',
        question: 'What time of day is described in the passage?',
        passage: 'The sun rose over the quiet village, casting golden rays on the sleeping houses. Birds began to sing their morning songs, and the world slowly came to life.',
        options: ['Noon', 'Evening', 'Sunrise', 'Midnight'],
        correctAnswer: 2,
        type: 'multiple-choice'
      }
    ]
  });

  const [currentExam, setCurrentExam] = useState(null);
  const [examProgress, setExamProgress] = useState({});
  const [examResults, setExamResults] = useState({});

  const startExam = (examId) => {
    const exam = exams.find(e => e.id === examId);
    setCurrentExam(exam);
    setExamProgress({
      currentQuestion: 0,
      answers: {},
      startTime: new Date()
    });
  };

  const submitAnswer = (questionId, answer) => {
    setExamProgress(prev => ({
      ...prev,
      answers: {
        ...prev.answers,
        [questionId]: answer
      }
    }));
  };

  const nextQuestion = () => {
    setExamProgress(prev => ({
      ...prev,
      currentQuestion: prev.currentQuestion + 1
    }));
  };

  const previousQuestion = () => {
    setExamProgress(prev => ({
      ...prev,
      currentQuestion: Math.max(0, prev.currentQuestion - 1)
    }));
  };

  const submitExam = () => {
    if (!currentExam) return;

    const questions = examQuestions[currentExam.id] || [];
    const answers = examProgress.answers;
    let correct = 0;
    
    questions.forEach(question => {
      if (answers[question.id] === question.correctAnswer) {
        correct++;
      }
    });

    const result = {
      examId: currentExam.id,
      examTitle: currentExam.title,
      totalQuestions: questions.length,
      correctAnswers: correct,
      score: Math.round((correct / questions.length) * 100),
      timeSpent: Math.round((new Date() - examProgress.startTime) / 1000 / 60),
      completedAt: new Date()
    };

    setExamResults(prev => ({
      ...prev,
      [currentExam.id]: result
    }));

    return result;
  };

  const value = {
    exams,
    examQuestions,
    currentExam,
    examProgress,
    examResults,
    startExam,
    submitAnswer,
    nextQuestion,
    previousQuestion,
    submitExam
  };

  return (
    <ExamContext.Provider value={value}>
      {children}
    </ExamContext.Provider>
  );
};
